/**
 * Cabinet compositor — off-screen render + presentation blit.
 *
 * Cartridges (and the boot/selector/overlay code) all render to an
 * **off-screen** canvas at their declared logical resolution
 * (480×270 / 960×540 / etc.). The shell then blits the off-screen
 * canvas to the **visible** canvas at the visible canvas's device-pixel
 * size, with smoothing toggled per cartridge taste:
 *
 *   - pixelated cartridges → nearest-neighbor blit (crisp pixel art)
 *   - vector cartridges    → bilinear blit (smooth strokes)
 *
 * This decouples logical render resolution from display resolution and
 * eliminates the fractional-pixel artifacts that plagued the previous
 * `image-rendering: pixelated`-on-the-visible-canvas approach:
 *
 *   - At odd scale factors (common on low-DPI monitors and tablets),
 *     `image-rendering: pixelated` produced inconsistent floor/ceil
 *     per logical pixel — jaggy text and ringing on detail-dense
 *     canvas content.
 *   - The previous canvas-snap fix forced an integer scale and
 *     letterboxed the leftover, sacrificing screen space for
 *     pixel-perfect rendering.
 *   - This compositor fills the entire bezel cleanly AND keeps pixel
 *     art crisp where the cartridge asked for it. Best of both.
 *
 * Lifecycle:
 *   - `attachCompositor(visibleCanvas, opts)` once at boot. Wires a
 *     ResizeObserver on the canvas's parent so the visible canvas's
 *     drawing buffer matches the display box × devicePixelRatio.
 *   - `getOffscreenContext()` — hand this to every render site
 *     (`runBootSequence`, `runSelector`, `startLoop`, `runPauseOverlay`,
 *     `runPressStart`).
 *   - `setLogicalResolution(w, h)` — call when a cartridge launches /
 *     teardown swaps the logical resolution.
 *   - `setSmoothing(smooth)` — toggle nearest-neighbor vs bilinear blit.
 *   - `present()` — call at the end of every rAF frame to blit the
 *     off-screen canvas to the visible canvas. Idempotent during the
 *     same frame; cheap to call multiple times.
 */

interface CompositorState {
  visible: HTMLCanvasElement;
  visibleCtx: CanvasRenderingContext2D;
  offscreen: HTMLCanvasElement;
  offscreenCtx: CanvasRenderingContext2D;
  observer: ResizeObserver | null;
  smoothing: boolean;
}

let state: CompositorState | null = null;

/**
 * Compute and apply the visible canvas's drawing-buffer size: the CSS
 * display box × devicePixelRatio so the blit lands on native pixels.
 * Idempotent — only writes when the values change.
 */
function syncVisibleBufferSize(): void {
  if (!state) return;
  const canvas = state.visible;
  const rect = canvas.getBoundingClientRect();
  const dpr = globalThis.devicePixelRatio || 1;
  const targetW = Math.max(1, Math.round(rect.width * dpr));
  const targetH = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== targetW) canvas.width = targetW;
  if (canvas.height !== targetH) canvas.height = targetH;
  // Drawing-buffer resize resets context state — re-apply smoothing.
  state.visibleCtx.imageSmoothingEnabled = state.smoothing;
  state.visibleCtx.imageSmoothingQuality = 'high';
}

/**
 * Attach the compositor to a visible canvas element. Builds the
 * off-screen canvas, wires the resize observer, and computes the
 * initial buffer size.
 *
 * @param visibleCanvas   The DOM-attached `<canvas id="game">`.
 * @param logicalW        Initial logical resolution width (cabinet baseline).
 * @param logicalH        Initial logical resolution height.
 */
export function attachCompositor(
  visibleCanvas: HTMLCanvasElement,
  logicalW: number,
  logicalH: number
): void {
  if (state && state.visible === visibleCanvas) return;
  detachCompositor();

  const visibleCtx = visibleCanvas.getContext('2d');
  if (!visibleCtx) {
    throw new Error('compositor: visible canvas getContext("2d") returned null');
  }

  const offscreen = document.createElement('canvas');
  offscreen.width = logicalW;
  offscreen.height = logicalH;
  const offscreenCtx = offscreen.getContext('2d');
  if (!offscreenCtx) {
    throw new Error('compositor: offscreen canvas getContext("2d") returned null');
  }
  // Off-screen is the logical drawing surface — pixel art cartridges
  // disable smoothing here when they draw; vector cartridges enable it.
  // Default to OFF so the boot sequence (pixel-art) starts clean.
  offscreenCtx.imageSmoothingEnabled = false;

  state = {
    visible: visibleCanvas,
    visibleCtx,
    offscreen,
    offscreenCtx,
    observer: null,
    smoothing: false,
  };

  syncVisibleBufferSize();

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => {
      syncVisibleBufferSize();
    });
    observer.observe(visibleCanvas);
    state.observer = observer;
  }
}

/**
 * Tear down. Test-friendly; production never calls this.
 */
export function detachCompositor(): void {
  if (!state) return;
  if (state.observer) {
    state.observer.disconnect();
  }
  state = null;
}

/**
 * The 2D context cartridges and shell code render into. Always the
 * off-screen ctx — never hand out the visible ctx.
 */
export function getOffscreenContext(): CanvasRenderingContext2D {
  if (!state) {
    throw new Error('compositor: getOffscreenContext() before attach');
  }
  return state.offscreenCtx;
}

/**
 * The DOM-attached <canvas id="game">. Use this to attach pointer event
 * listeners — pointer events fire on the visible canvas at CSS pixel
 * coordinates, but you'll want to map them to the offscreen canvas's
 * LOGICAL coordinate space (not the visible canvas's drawing buffer,
 * which is sized to display × devicePixelRatio).
 */
export function getVisibleCanvas(): HTMLCanvasElement {
  if (!state) {
    throw new Error('compositor: getVisibleCanvas() before attach');
  }
  return state.visible;
}

/**
 * Resize the off-screen canvas to a new logical resolution. The
 * launcher calls this on cartridge enter (cartridge's resolution) and
 * exit (cabinet baseline).
 */
export function setLogicalResolution(w: number, h: number): void {
  if (!state) return;
  if (state.offscreen.width === w && state.offscreen.height === h) return;
  state.offscreen.width = w;
  state.offscreen.height = h;
  // Canvas resize resets context state. Default smoothing off; the
  // launcher / boot / selector reapply per-frame anyway.
  state.offscreenCtx.imageSmoothingEnabled = false;
}

/**
 * Toggle smoothing for the **blit** from offscreen to visible.
 *  - `false` (default): nearest-neighbor — keeps pixel art crisp at
 *    arbitrary scale factors.
 *  - `true`: bilinear — smooth strokes for vector cartridges.
 *
 * The launcher calls this per cartridge based on `module_.pixelated`.
 */
export function setSmoothing(smooth: boolean): void {
  if (!state) return;
  state.smoothing = smooth;
  state.visibleCtx.imageSmoothingEnabled = smooth;
  if (smooth) {
    state.visibleCtx.imageSmoothingQuality = 'high';
  }
}

/**
 * Blit the offscreen canvas to the visible canvas. Called at the end of
 * every rAF frame from boot / selector / loop / overlays / press-start.
 *
 * The visible canvas's drawing buffer is sized to the CSS display box
 * times devicePixelRatio, so this blit lands on native device pixels.
 * Crucially, the blit **preserves the offscreen canvas's aspect ratio**
 * by computing a centered, aspect-correct destination rect inside the
 * visible buffer — any non-aspect-matching rounding of the CSS box
 * shows as a thin letterbox bar, not a stretched image. Previously the
 * blit covered the full visible buffer regardless of aspect, which made
 * the canvas content look subtly stretched horizontally on viewports
 * where the bezel resolved to a slightly-non-16:9 box (verified on
 * desktop 1080p with chrome visible, 2026-05-14).
 */
export function present(): void {
  if (!state) return;
  const v = state.visibleCtx;
  const vw = state.visible.width;
  const vh = state.visible.height;
  const ow = state.offscreen.width;
  const oh = state.offscreen.height;
  // Compute the largest aspect-correct dest rect that fits inside the
  // visible buffer. Center it; any leftover is letterboxed by the
  // bezel's matte color (we don't paint it — saves a fillRect).
  const srcAR = ow / oh;
  const dstAR = vw / vh;
  let dw: number;
  let dh: number;
  if (srcAR > dstAR) {
    // Source wider than destination — fit to width, letterbox top/bottom.
    dw = vw;
    dh = Math.round(vw / srcAR);
  } else {
    // Source taller (or equal) — fit to height, letterbox left/right.
    dh = vh;
    dw = Math.round(vh * srcAR);
  }
  const dx = Math.round((vw - dw) / 2);
  const dy = Math.round((vh - dh) / 2);
  // Clear letterbox bars so we don't show stale pixels from a previous
  // frame with a different dest rect (e.g., cartridge launch resizes
  // the offscreen).
  if (dw < vw || dh < vh) {
    v.fillStyle = '#000';
    v.fillRect(0, 0, vw, vh);
  }
  // `setSmoothing(false)` is the default; nearest-neighbor stretches
  // logical pixels to fill the dest rect cleanly. With smoothing on,
  // the same drawImage call produces bilinear interpolation.
  v.drawImage(state.offscreen, dx, dy, dw, dh);
}

// Test-only — peek at internal state for assertions.
export const __test = {
  getState: (): CompositorState | null => state,
};
